import React, { useCallback, useEffect, useState } from "react";
import { ReactFlow, Background, Controls, MiniMap, addEdge, useNodesState, useEdgesState, BackgroundVariant, useReactFlow } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { mockNodes, mockEdges } from "~/lib/mockData";
import SourceNode from "~/components/SourceNode";
import EntityNode from "~/components/EntityNode";
import TrendNode from "~/components/TrendNode";
import FlowEdge from "~/components/FlowEdge";
import NodeDetails from "~/components/NodeDetails";
import DataFlowControlCard from "~/components/DataFlowControlCard";
import styled from "styled-components";
import { Box, Text } from "~/components-shared";
import { genericColor } from "~/lib/resolvers";
import { AnimClassEnum, AnimOrderEnum } from "~/types/enums";
import { useTranslation } from "react-i18next";
const Wrapper = styled.div`
  width: 100%;
  height: 100vh;
  background-color: transparent;
  position: relative;
`;
const Color = styled.i`
  background-color: ${_ref => {
  let {
    $color,
    theme
  } = _ref;
  return genericColor(theme, $color);
}};
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 0.5em;
  display: inline-block;
`;
const StyledMiniMap = styled(MiniMap)`
  border-radius: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.shape.borderRadius.primary;
}};
  background-color: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return genericColor(theme, "backgroundDark");
}};
  overflow: hidden;
`;
const StyledControls = styled(Controls)`
  border-radius: ${_ref4 => {
  let {
    theme
  } = _ref4;
  return theme.shape.borderRadius.primary;
}};
  background-color: ${_ref5 => {
  let {
    theme
  } = _ref5;
  return genericColor(theme, "backgroundDark");
}};
  overflow: hidden;

  & button {
    background-color: ${_ref6 => {
  let {
    theme
  } = _ref6;
  return genericColor(theme, "backgroundDark");
}};
    border-color: ${_ref7 => {
  let {
    theme
  } = _ref7;
  return genericColor(theme, "backgroundDark");
}};
  }
`;
const StyledBox = styled(Box)`
  position: absolute;
  left: 0;
  top: 0;
  z-index: 10;
`;
const Legend = styled(Box)`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 1em;
  border-radius: ${_ref8 => {
  let {
    theme
  } = _ref8;
  return theme.shape.borderRadius.primary;
}};
  z-index: 10;
`;
const nodeTypes = {
  source: SourceNode,
  entity: EntityNode,
  trend: TrendNode
};
const edgeTypes = {
  flow: FlowEdge
};

// Convert mock data to ReactFlow format
const initialNodes = (() => {
  const sourceNodes = mockNodes.filter(n => n.type === "source");
  const entityNodes = mockNodes.filter(n => n.type === "entity");
  const trendNodes = mockNodes.filter(n => n.type === "trend");
  const sourceSpacing = 280;
  const totalSourceHeight = (sourceNodes.length - 1) * sourceSpacing;
  const entitySpacing = entityNodes.length > 1 ? totalSourceHeight / (entityNodes.length - 1) : 0;
  const trendSpacing = trendNodes.length > 1 ? totalSourceHeight / (trendNodes.length - 1) : 0;
  let sourceIndex = 0;
  let entityIndex = 0;
  let trendIndex = 0;
  return mockNodes.map(node => {
    let position;
    if (node.type === "source") {
      position = {
        x: 50,
        y: sourceIndex * sourceSpacing
      };
      sourceIndex += 1;
    } else if (node.type === "entity") {
      position = {
        x: 500,
        y: entityIndex * entitySpacing
      };
      entityIndex += 1;
    } else {
      position = {
        x: 1000,
        y: trendIndex * trendSpacing
      };
      trendIndex += 1;
    }
    return {
      id: node.id,
      type: node.type,
      position,
      data: node
    };
  });
})();
const initialEdges = mockEdges.map(edge => ({
  id: edge.id,
  source: edge.source,
  target: edge.target,
  type: "flow",
  animated: edge.animated || false,
  data: {
    weight: edge.weight || 1,
    animated: edge.animated || false,
    sourceType: edge.sourceType || "source"
  }
}));
const DataFlow = () => {
  const {
    t
  } = useTranslation();
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedSubdomain, setSelectedSubdomain] = useState("All");
  const onConnect = useCallback(params => setEdges(eds => addEdge(params, eds)), [setEdges]);
  const {
    fitView
  } = useReactFlow();
  useEffect(() => {
    setTimeout(() => {
      fitView({
        duration: 500,
        padding: {
          top: 0.7,
          right: 0,
          bottom: 0.2,
          left: 0
        }
      });
    }, 1000);
  }, []);
  const onNodeClick = useCallback((_, node) => {
    setSelectedNode(node.data);
  }, []);
  const handleSubdomainChange = subdomain => {
    setSelectedSubdomain(subdomain);
    if (subdomain === "All") {
      setNodes(initialNodes);
    } else {
      const filteredNodes = initialNodes.filter(node => node.data.subdomain === subdomain || node.data.subdomain === "All");
      setNodes(filteredNodes);
    }
  };
  return /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement(ReactFlow, {
    nodes: nodes,
    edges: edges,
    onNodesChange: onNodesChange,
    onEdgesChange: onEdgesChange,
    onConnect: onConnect,
    onNodeClick: onNodeClick,
    nodeTypes: nodeTypes,
    edgeTypes: edgeTypes,
    fitView: true,
    minZoom: 0.1,
    maxZoom: 2
  }, /*#__PURE__*/React.createElement(StyledBox, {
    direction: "column",
    s: {
      pt: 12,
      pl: 14
    }
  }, /*#__PURE__*/React.createElement(Text, {
    element: "h1",
    s: {
      mb: 3
    },
    className: `${AnimOrderEnum.ONE} ${AnimClassEnum.POP_RIGHT_FADE}`
  }, t("DataFlow.overview")), /*#__PURE__*/React.createElement(DataFlowControlCard, {
    selectedSubdomain: selectedSubdomain,
    onSubdomainChange: handleSubdomainChange,
    s: {
      p: 2
    }
  })), /*#__PURE__*/React.createElement(StyledControls, null), /*#__PURE__*/React.createElement(Background, {
    variant: BackgroundVariant.Dots,
    gap: 20,
    size: 1,
    color: "hsl(224,21%,32%)"
  }), /*#__PURE__*/React.createElement(StyledMiniMap, {
    nodeColor: node => {
      switch (node.type) {
        case "source":
          return "hsl(199 89% 48%)";
        case "entity":
          return "hsl(262 83% 58%)";
        case "trend":
          return "hsl(31 97% 62%)";
        default:
          return "hsl(223 47% 11%)";
      }
    }
  })), /*#__PURE__*/React.createElement(NodeDetails, {
    node: selectedNode,
    onClose: () => setSelectedNode(null)
  }), /*#__PURE__*/React.createElement(Legend, {
    gap: 2,
    color: "backgroundDark",
    s: {
      px: 3,
      py: 2
    }
  }, /*#__PURE__*/React.createElement(Text, null, /*#__PURE__*/React.createElement(Color, {
    $color: "hsl(199 89% 48%)"
  }), "Data Sources"), /*#__PURE__*/React.createElement(Text, {
    color: "text"
  }, /*#__PURE__*/React.createElement(Color, {
    $color: "hsl(262 83% 58%)"
  }), "Entities/Seeds"), /*#__PURE__*/React.createElement(Text, null, /*#__PURE__*/React.createElement(Color, {
    $color: "hsl(31 97% 62%)"
  }), "Trends")));
};
export default DataFlow;